import { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Filter, MapPin, Navigation, RotateCcw, ChevronDown, ChevronUp, List } from 'lucide-react';
import { useAuth } from '../../stores/auth';
import api from '../../utils/api';
import PriceRangeSlider from './PriceRangeSlider';
import CityGeographicFilter from './CityGeographicFilter';
import { City } from '../../types';
import { ADMIN_LECCE_COORDS } from '../../config/filters';
import { useGlassmorphism } from '../../hooks/useGlassmorphism';

interface SidebarFiltersProps {
  filters: {
    brand: string;
    model: string;
    min_price: string;
    max_price: string;
    year: string;
    min_length: string;
    max_length: string;
    engine_type: string;
    fuel_type: string;
    condition: string;
    boat_type_code: string;
    accessory_type_code: string;
  };
  setFilters: (filters: any) => void;
  onPriceFilterChange?: (min: string, max: string) => void;
  location: { lat: number; lng: number } | null;
  setLocation: (location: { lat: number; lng: number } | null) => void;
  locationStatus: string;
  setLocationStatus: (status: string) => void;
  radius: number;
  setRadius: (radius: number) => void;
  useLocation: boolean;
  setUseLocation: (use: boolean) => void;
  useFixedLocation: boolean;
  setUseFixedLocation: (use: boolean) => void;
   availableOnly: boolean;
   setAvailableOnly: (available: boolean) => void;
   statusFilter: string;
   setStatusFilter: (status: string) => void;
   selectedCity: City | null;
  setSelectedCity: (city: City | null) => void;
  selectedProvince: string;
  setSelectedProvince: (province: string) => void;
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  className?: string;
  showViewAllLink?: boolean;
}

const kilometerRanges = [
  { label: 'Tutte', max: -1, color: 'var(--color-text-muted)' },
  { label: '20 km', max: 20, color: 'var(--color-success)' },
  { label: '50 km', max: 50, color: 'var(--color-success)' },
  { label: '100 km', max: 100, color: 'var(--color-warning)' },
  { label: '150 km', max: 150, color: 'var(--color-warning)' },
  { label: '200 km', max: 200, color: 'var(--color-error)' },
  { label: '500 km', max: 500, color: 'bg-purple-500' },
];

// @ts-expect-error unused const
const sortOptions = [
  { value: 'created_desc', label: 'Più recenti' },
  { value: 'created_asc', label: 'Meno recenti' },
  { value: 'price_asc', label: 'Prezzo crescente' },
  { value: 'price_desc', label: 'Prezzo decrescente' },
  { value: 'year_desc', label: 'Anno decrescente' },
  { value: 'length_desc', label: 'Lunghezza decrescente' },
];

export default function SidebarFilters({
  filters,
  setFilters,
  onPriceFilterChange,
  location,
  setLocation,
  locationStatus,
  setLocationStatus,
  radius,
  setRadius,
  useLocation,
  setUseLocation,
  useFixedLocation,
  setUseFixedLocation,
  availableOnly,
   setAvailableOnly,
   statusFilter,
   setStatusFilter,
   selectedCity,
  setSelectedCity,
  selectedProvince,
  setSelectedProvince,
  selectedRegion,
  setSelectedRegion,
  className = '',
  showViewAllLink = false,
}: SidebarFiltersProps) {
  const { glass: _glass, isGlassEnabled: _isGlassEnabled } = useGlassmorphism();
  const { user } = useAuth();
  
  // Get dealer's city for display - use citta or city from user object
  const dealerCity = String(user?.citta || user?.city || '');
  
  const [expandedSections, setExpandedSections] = useState({
    search: true,
    location: true,
    price: true,
    specs: true,
    condition: true,
    tipologia: true,
  });
  const [manualCity, setManualCity] = useState('');
  const [citySearchResults, setCitySearchResults] = useState<any[]>([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Fetch filter options from DB
  const { data: filterOptions } = useQuery('filterOptions', () =>
    api.get('/filters/options').then((res: any) => res.data?.data || { brands: [], models: [], years: [], engine_types: [], fuel_types: [], conditions: [] }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch price range from DB
  const { data: priceRange } = useQuery('priceRange', () =>
    api.get('/filters/price-range').then((res: any) => res.data?.data || { min_price: 0, max_price: 1000000, min_length: 0, max_length: 50 }),
    { staleTime: 5 * 60 * 1000 }
  );

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('Geolocalizzazione non supportata');
      return;
    }

    setLocationStatus('Localizzazione in corso...');
     navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(newLocation);
          setUseLocation(true);
          setUseFixedLocation(false);
          setLocationStatus('Posizione impostata!');
          setTimeout(() => setLocationStatus(''), 2000);
        },
      (error) => {
        let message = 'Errore nella localizzazione';
        if (error.code === error.PERMISSION_DENIED) message = 'Permesso negato';
        else if (error.code === error.POSITION_UNAVAILABLE) message = 'Posizione non disponibile';
        else if (error.code === error.TIMEOUT) message = 'Timeout';
        setLocationStatus(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleUseFixedLocation = () => {
    const lecceLocation = ADMIN_LECCE_COORDS;
    setLocation(lecceLocation);
    setUseFixedLocation(true);
    setUseLocation(false);
    setLocationStatus(`Posizione fissa: ${dealerCity || 'Lecce'}`);
    setTimeout(() => setLocationStatus(''), 2000);
  };

  const handleManualCitySearch = async (value: string) => {
    setManualCity(value);
    if (value.length < 2) {
      setCitySearchResults([]);
      setShowCityDropdown(false);
      return;
    }

    try {
      const response = await api.get('/cities', { params: { search: value } });
      // API returns {success: true, data: [...]}
      setCitySearchResults(response.data?.data || []);
      setShowCityDropdown(true);
    } catch (error) {
      console.error('Errore ricerca città:', error);
      setCitySearchResults([]);
    }
  };

   const handleCitySelect = (city: any) => {
     const cityLocation = { lat: parseFloat(city.lat), lng: parseFloat(city.lng) };
     setLocation(cityLocation);
     setUseLocation(false);
     setUseFixedLocation(false);
     setManualCity(`${city.name} (${city.province})`);
     setShowCityDropdown(false);
     setLocationStatus(`Città: ${city.name}`);
     setTimeout(() => setLocationStatus(''), 2000);
   };

  const handleResetLocation = () => {
    setUseLocation(false);
    setUseFixedLocation(false);
    setLocation(null);
    setLocationStatus('');
    setManualCity('');
    setShowCityDropdown(false);
  };

  const handleResetFilters = () => {
    setFilters({
      brand: '',
      model: '',
      min_price: '',
      max_price: '',
      year: '',
      min_length: '',
      max_length: '',
      engine_type: '',
      fuel_type: '',
      condition: '',
      boat_type_code: '',
      accessory_type_code: '',
    });
    handleResetLocation();
    setRadius(-1);
    setAvailableOnly(false);
  };

  const hasActiveFilters = () => {
    return (
      filters.brand !== '' ||
      filters.model !== '' ||
      filters.min_price !== '' ||
      filters.max_price !== '' ||
      filters.year !== '' ||
      filters.min_length !== '' ||
      filters.max_length !== '' ||
      filters.engine_type !== '' ||
      filters.fuel_type !== '' ||
      filters.condition !== '' ||
      filters.boat_type_code !== '' ||
      filters.accessory_type_code !== '' ||
      useLocation ||
      useFixedLocation ||
      location !== null ||
      availableOnly
    );
  };

  const minPrice = priceRange?.min_price || 0;
  const maxPrice = priceRange?.max_price || 1000000;
  // @ts-expect-error unused
  const _minLength = priceRange?.min_length || 0;
  // @ts-expect-error unused
  const _maxLength = priceRange?.max_length || 50;

  return (
    <div className={`rounded-xl shadow-sm ${className}`} style={{ background: '#ffffff' }} data-testid="sidebar-filters">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--glass-text-primary, #242424)' }}>Filtri</h2>
          {hasActiveFilters() && (
            <button
              onClick={handleResetFilters}
              data-testid="clear-filters"
              className="text-sm flex items-center gap-1 transition-colors"
              style={{ color: 'var(--glass-primary, #0099ff)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--glass-primary, #0077cc)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--glass-primary, #0099ff)'}
            >
              <RotateCcw className="w-4 h-4" />
              Resetta
            </button>
          )}
        </div>
      </div>

      <div className="divide-y">
        {/* Search Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('search')}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>Ricerca</h3>
            {expandedSections.search ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
          </button>
          {expandedSections.search && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Cerca</label>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--glass-text-disabled)' }} />
                  <input
                    type="text"
                    placeholder="Marca, modello..."
                    value={filters.brand}
                    onChange={(e) => setFilters({ ...filters, brand: e.target.value, model: '' })}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: 'var(--glass-card-bg, #ffffff)',
                      border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                      color: 'var(--glass-text-primary, #242424)'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Location Section - Sticky */}
        <div className="p-4 sticky-top z-50" style={{ backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <button
            onClick={() => toggleSection('location')}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="font-medium flex items-center gap-2" style={{ color: 'var(--glass-text-primary, #242424)' }}>
              <MapPin className="w-4 h-4" style={{ color: 'var(--glass-primary, #0099ff)' }} />
              Posizione
            </h3>
            {expandedSections.location ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
          </button>
          {expandedSections.location && (
            <div className="mt-4 space-y-4">
              {/* Location Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleUseMyLocation}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition-all"
                  style={{
                    backgroundColor: useLocation ? 'var(--glass-primary-light)' : 'var(--glass-card-bg, #ffffff)',
                    color: useLocation ? 'var(--glass-primary, #0099ff)' : 'var(--glass-text-primary, #242424)',
                    border: '1px solid ' + (useLocation ? 'var(--glass-primary, rgba(0, 153, 255, 0.3))' : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
                  }}
                >
                  <Navigation className="w-4 h-4" />
                  GPS
                </button>
                <button
                  onClick={handleUseFixedLocation}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition-all"
                  style={{
                    backgroundColor: useFixedLocation ? 'var(--glass-success-light)' : 'var(--glass-card-bg, #ffffff)',
                    color: useFixedLocation ? 'var(--glass-success, #22c55e)' : 'var(--glass-text-primary, #242424)',
                    border: '1px solid ' + (useFixedLocation ? 'var(--glass-success, rgba(34, 197, 94, 0.3))' : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
                  }}
                >
                  <MapPin className="w-4 h-4" />
                  {dealerCity || 'Lecce'}
                </button>
              </div>

              {/* City Search */}
              <div className="relative">
                <input
                  type="text"
                  value={manualCity}
                  onChange={(e) => handleManualCitySearch(e.target.value)}
                  onFocus={() => showCityDropdown && citySearchResults.length > 0 && setShowCityDropdown(true)}
                  placeholder="Cerca città..."
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                />
                {showCityDropdown && citySearchResults.length > 0 && (
                  <div 
                    className="absolute z-20 w-full mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto"
                    style={{
                      backgroundColor: 'var(--glass-card-bg, #ffffff)',
                      border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))'
                    }}
                  >
                    {citySearchResults.map((city: any) => (
                      <button
                        key={city.id}
                        onClick={() => handleCitySelect(city)}
                        className="w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors"
                        style={{ color: 'var(--glass-text-primary, #242424)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--glass-hover-bg)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span className="font-medium">{city.name}</span>
                        <span className="text-xs" style={{ color: 'var(--glass-text-secondary, #898989)' }}>{city.province}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Location Status */}
              {locationStatus && (
                <div 
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    backgroundColor: locationStatus.includes('Errore') || locationStatus.includes('negato') 
                      ? 'var(--glass-danger-light)' 
                      : 'var(--glass-primary-light)',
                    color: locationStatus.includes('Errore') || locationStatus.includes('negato') 
                      ? 'var(--glass-danger)' 
                      : 'var(--glass-primary, #0099ff)'
                  }}
                >
                  {locationStatus}
                </div>
              )}

              {/* Distance Ranges */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--glass-text-primary, #242424)' }}>Raggio di ricerca</label>
                <div className="grid grid-cols-3 gap-2">
                  {kilometerRanges.map((range) => (
                    <button
                      key={range.max}
                      onClick={() => setRadius(range.max)}
                      className="py-2 px-2 rounded-lg text-xs font-medium transition-all"
                      style={{
                        backgroundColor: radius === range.max && (useFixedLocation || useLocation || location)
                          ? 'var(--glass-primary-light)'
                          : 'var(--glass-card-bg, #ffffff)',
                        color: radius === range.max && (useFixedLocation || useLocation || location)
                          ? 'var(--glass-primary, #0099ff)'
                          : 'var(--glass-text-primary, #242424)',
                        border: '1px solid ' + (radius === range.max && (useFixedLocation || useLocation || location)
                          ? 'var(--glass-primary, rgba(0, 153, 255, 0.3))'
                          : 'var(--glass-card-border, #e5e7eb)')
                      }}
                      onMouseEnter={(e) => {
                        if (!(radius === range.max && (useFixedLocation || useLocation || location))) {
                          e.currentTarget.style.backgroundColor = 'var(--glass-hover-bg, #f9fafb)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!(radius === range.max && (useFixedLocation || useLocation || location))) {
                          e.currentTarget.style.backgroundColor = 'var(--glass-card-bg, #ffffff)';
                        }
                      }}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear Location */}
              {(useLocation || useFixedLocation || location) && (
                <button
                  onClick={handleResetLocation}
                  className="w-full py-2 text-sm rounded-lg transition-colors border"
                  style={{
                    color: 'var(--glass-danger)',
                    borderColor: 'var(--glass-danger-light)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--glass-danger-light)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Disattiva posizione
                </button>
              )}

              {/* Available Only Toggle */}
              <button
                onClick={() => setAvailableOnly(!availableOnly)}
                className="w-full py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
                style={{
                  backgroundColor: availableOnly ? 'var(--glass-success-light)' : 'var(--glass-card-bg, #ffffff)',
                  color: availableOnly ? 'var(--glass-success, #22c55e)' : 'var(--glass-text-primary, #242424)',
                  border: '1px solid ' + (availableOnly ? 'var(--glass-success, rgba(34, 197, 94, 0.3))' : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
                }}
                onMouseEnter={(e) => {
                  if (!availableOnly) {
                    e.currentTarget.style.backgroundColor = 'var(--glass-hover-bg, #f9fafb)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!availableOnly) {
                    e.currentTarget.style.backgroundColor = 'var(--glass-card-bg, #ffffff)';
                  }
                }}
              >
                <span 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: availableOnly ? 'var(--glass-success, #22c55e)' : 'var(--glass-text-disabled, #9ca3af)' }} 
                />
                 Solo disponibili
               </button>

                {/* Status Filter Buttons */}
                <div className="mt-3">
                  <span className="text-xs text-gray-500 font-medium mb-2 block">Stato</span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setStatusFilter('')}
                      className={`px-3 py-1.5 text-xs font-medium bt-badge transition-colors ${
                        statusFilter === '' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Tutti
                    </button>
                    <button
                      onClick={() => setStatusFilter('available')}
                      className={`px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1 ${
                        statusFilter === 'available' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${statusFilter === 'available' ? 'bg-white' : 'bg-green-500'}`} />
                      Disp.
                    </button>
                    <button
                      onClick={() => setStatusFilter('reserved')}
                      className={`px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1 ${
                        statusFilter === 'reserved' 
                          ? 'bg-yellow-500 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${statusFilter === 'reserved' ? 'bg-white' : 'bg-yellow-500'}`} />
                      Pren.
                    </button>
                    <button
                      onClick={() => setStatusFilter('sold')}
                      className={`px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1 ${
                        statusFilter === 'sold' 
                          ? 'bg-red-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${statusFilter === 'sold' ? 'bg-white' : 'bg-red-500'}`} />
                      Vend.
                    </button>
                  </div>
                </div>
             </div>
          )}
        </div>

        {/* Filtro Geografico Avanzato */}
        <div className="p-4" style={{ backgroundColor: 'var(--glass-secondary-bg)' }}>
          <CityGeographicFilter
            selectedCity={selectedCity}
            setSelectedCity={setSelectedCity}
            selectedProvince={selectedProvince}
            setSelectedProvince={setSelectedProvince}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            useLocation={useLocation}
            setUseLocation={setUseLocation}
            useFixedLocation={useFixedLocation}
            setUseFixedLocation={setUseFixedLocation}
            location={location}
            setLocation={setLocation}
            locationStatus={locationStatus}
            setLocationStatus={setLocationStatus}
            currentRadius={radius}
            onRadiusChange={setRadius}
          />
        </div>

        {/* Price Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('price')}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>Prezzo (€)</h3>
            {expandedSections.price ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
          </button>
          {expandedSections.price && (
            <div className="mt-4 space-y-4">
              {/* Price Range Slider from DB */}
              <PriceRangeSlider
                data-testid="price-filter"
                minPrice={minPrice}
                maxPrice={maxPrice}
                onPriceChange={(min, max) => {
                  if (onPriceFilterChange) {
                    onPriceFilterChange(min, max);
                  } else {
                    setFilters({ ...filters, min_price: min, max_price: max });
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Specs Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('specs')}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>Specifiche</h3>
            {expandedSections.specs ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
          </button>
          {expandedSections.specs && (
            <div className="mt-4 space-y-4">
              {/* Brand Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Marca</label>
                <select
                  data-testid="brand-filter"
                  value={filters.brand}
                  onChange={(e) => setFilters({ ...filters, brand: e.target.value, model: '' })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutte le marche</option>
                  {filterOptions?.brands?.map((brand: string) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              {/* Model Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Modello</label>
                <select
                  value={filters.model}
                  onChange={(e) => setFilters({ ...filters, model: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutti i modelli</option>
                  {filterOptions?.models?.map((model: string) => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Anno</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutti gli anni</option>
                  {filterOptions?.years?.map((year: number) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Length */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Lunghezza Min (m)</label>
                  <select
                    value={filters.min_length}
                    onChange={(e) => setFilters({ ...filters, min_length: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: 'var(--glass-card-bg, #ffffff)',
                      border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                      color: 'var(--glass-text-primary, #242424)'
                    }}
                  >
                    <option value="">Min</option>
                    {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 35, 40, 50].map((val) => (
                      <option key={val} value={val}>{val}m</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Lunghezza Max (m)</label>
                  <select
                    value={filters.max_length}
                    onChange={(e) => setFilters({ ...filters, max_length: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: 'var(--glass-card-bg, #ffffff)',
                      border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                      color: 'var(--glass-text-primary, #242424)'
                    }}
                  >
                    <option value="">Max</option>
                    {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 35, 40, 50, 100].map((val) => (
                      <option key={val} value={val}>{val}m</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Engine Type */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Motorizzazione</label>
                <select
                  value={filters.engine_type}
                  onChange={(e) => setFilters({ ...filters, engine_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutte</option>
                  {filterOptions?.engine_types?.map((type: string) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Fuel Type */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Carburante</label>
                <select
                  value={filters.fuel_type}
                  onChange={(e) => setFilters({ ...filters, fuel_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutti</option>
                  {filterOptions?.fuel_types?.map((type: string) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Condition Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('condition')}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>Condizione</h3>
            {expandedSections.condition ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
          </button>
          {expandedSections.condition && (
            <div className="mt-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--glass-text-primary, #242424)' }}>Stato</label>
                <select
                  value={filters.condition}
                  onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--glass-card-bg, #ffffff)',
                    border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                >
                  <option value="">Tutti</option>
                  {filterOptions?.conditions?.map((condition: string) => (
                    <option key={condition} value={condition}>{condition}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* TIPOLOGIA Section */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--glass-card-border, #e5e7eb)' }}>
        <button
          onClick={() => toggleSection('tipologia')}
          className="flex items-center justify-between w-full"
        >
          <h3 className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>Tipologia</h3>
          {expandedSections.tipologia ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
        </button>
        {expandedSections.tipologia && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2">
              {[
                { code: 'GOM', nome: 'Gommone', color: 'var(--color-primary) var(--color-bg-primary)' },
                { code: 'RIB', nome: 'RIB', color: 'bg-cyan-100 text-cyan-700' },
                { code: 'TEN', nome: 'Tender', color: 'bg-amber-100 text-amber-700' },
                { code: 'OPN', nome: 'Open', color: 'bg-emerald-100 text-emerald-700' },
                { code: 'CC', nome: 'Center Console', color: 'bg-violet-100 text-violet-700' },
                { code: 'FLY', nome: 'Flybridge', color: 'bg-indigo-100 text-indigo-700' },
                { code: 'SPT', nome: 'Sport', color: 'var(--color-warning) var(--color-bg-primary)' },
                { code: 'FSH', nome: 'Fishing', color: 'var(--color-error) var(--color-bg-primary)' },
                { code: 'CAT', nome: 'Catamarano', color: 'bg-pink-100 text-pink-700' },
                { code: 'MON', nome: 'Vela', color: 'bg-sky-100 text-sky-700' },
                { code: 'MYT', nome: 'Yacht', color: 'bg-purple-100 text-purple-700' },
              ].map((type) => (
                <button
                  key={type.code}
                  onClick={() => {
                    const current = filters.boat_type_code ? filters.boat_type_code.split(',') : [];
                    let newTypes;
                    if (current.includes(type.code)) {
                      newTypes = current.filter((t) => t !== type.code);
                    } else {
                      newTypes = [...current, type.code];
                    }
                    setFilters({ ...filters, boat_type_code: newTypes.join(',') });
                  }}
                  className={`px-3 py-1.5 text-xs font-medium bt-badge border-2 transition-all ${
                    filters.boat_type_code?.includes(type.code)
                      ? `${type.color} border-current`
                      : 'var(--color-bg-secondary) var(--color-text-secondary) var(--color-border) hover:var(--color-border)'
                  }`}
                >
                  {type.nome}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="p-4" style={{ backgroundColor: 'var(--glass-secondary-bg)' }}>
          <div className="text-xs mb-2" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Filtri attivi:</div>
          <div className="flex flex-wrap gap-1">
            {filters.brand && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                Marca: {filters.brand}
              </span>
            )}
            {filters.fuel_type && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                {filters.fuel_type}
              </span>
            )}
            {filters.engine_type && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                {filters.engine_type}
              </span>
            )}
            {filters.condition && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                {filters.condition}
              </span>
            )}
            {filters.accessory_type_code && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                Tipologia: {filters.accessory_type_code}
              </span>
            )}
            {filters.year && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-primary-light)',
                  color: 'var(--glass-primary, #0099ff)'
                }}
              >
                {filters.year}
              </span>
            )}
            {(useLocation || useFixedLocation || location) && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-success-light)',
                  color: 'var(--glass-success, #22c55e)'
                }}
              >
                Posizione attiva
              </span>
            )}
            {availableOnly && (
              <span 
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: 'var(--glass-success-light)',
                  color: 'var(--glass-success, #22c55e)'
                }}
              >
                Solo disponibili
              </span>
            )}
          </div>
        </div>
      )}

      {/* View All Boats Link */}
      {showViewAllLink && (
        <div className="p-4">
          <Link
            to={`/boats?${new URLSearchParams({
              ...(filters.brand && { brand: filters.brand }),
              ...(filters.model && { model: filters.model }),
              ...(filters.min_price && { min_price: filters.min_price }),
              ...(filters.max_price && { max_price: filters.max_price }),
              ...(filters.year && { year: filters.year }),
              ...(filters.min_length && { min_length: filters.min_length }),
              ...(filters.max_length && { max_length: filters.max_length }),
              ...(filters.engine_type && { engine_type: filters.engine_type }),
              ...(filters.fuel_type && { fuel_type: filters.fuel_type }),
              ...(filters.condition && { condition: filters.condition }),
              ...(filters.boat_type_code && { boat_type_code: filters.boat_type_code }),
              ...(filters.accessory_type_code && { accessory_type_code: filters.accessory_type_code }),
              ...(location && { lat: String(location.lat), lng: String(location.lng) }),
              ...(radius && { radius_km: String(radius) }),
              ...(availableOnly && { available_only: '1' }),
            }).toString()}`}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
          >
            <List className="w-4 h-4" />
            Vedi tutte le barche
          </Link>
        </div>
      )}
    </div>
  );
}
